import { createAsyncThunk, createEntityAdapter, createSlice, type EntityState } from '@reduxjs/toolkit';
import { RequestState, type UserConnection, type UserConnectionActionType } from '@alltrails/types/belay';
import { addActionsToConnection, setUserActionRequestState } from '../utils/belayHelpers';
import { userActionRequest } from '../utils/belayRequests';

const userConnectionsAdapter = createEntityAdapter({
  selectId: (userConnection: UserConnection) => userConnection.user.id
});

export const { selectById: getUserConnection } = userConnectionsAdapter.getSelectors<EntityState<UserConnection, string>>(state => state);

type UpdateUserConnectionArgs = {
  href: string;
  method: RequestInit['method'];
  userConnection: UserConnection;
  actionKey: UserConnectionActionType;
};

export const updateUserConnection = createAsyncThunk(
  'userConnections/updateUserConnection',
  async ({ href, method, userConnection: _userConnection, actionKey: _actionKey }: UpdateUserConnectionArgs) => {
    const data = await userActionRequest(href, method);
    const updatedUserConnection = addActionsToConnection(data);
    return updatedUserConnection;
  }
);

export const userConnectionsSlice = createSlice({
  name: 'userConnections',
  initialState: userConnectionsAdapter.getInitialState(),
  reducers: {
    addUserConnections: userConnectionsAdapter.setMany
  },
  extraReducers: builder => {
    builder.addCase(updateUserConnection.pending, (state, { meta }) => {
      const { userConnection, actionKey } = meta.arg;
      const updatedUserConnection = setUserActionRequestState(userConnection, actionKey, RequestState.Loading);
      userConnectionsAdapter.setOne(state, updatedUserConnection);
    });
    builder.addCase(updateUserConnection.fulfilled, (state, { payload }) => {
      userConnectionsAdapter.setOne(state, payload);
    });
    builder.addCase(updateUserConnection.rejected, (state, { meta }) => {
      const { userConnection, actionKey } = meta.arg;
      const updatedUserConnection = setUserActionRequestState(userConnection, actionKey, RequestState.Failed);
      userConnectionsAdapter.setOne(state, updatedUserConnection);
    });
  }
});

export const { addUserConnections } = userConnectionsSlice.actions;
export default userConnectionsSlice.reducer;
